/**
 * 品牌管理服务
 */

import axios from 'axios';
import { extractResultsFromResponse } from '../utils/apiResponse';

const API_BASE_URL = 'http://localhost:8000/api';

export interface Brand {
  id?: number;
  name: string;
  archetype: string;
  tone: string;
  core_message: string;
  audience: string;
  mandatory_phrases: string;
  forbidden_words: string;
  primary_color: string;
  secondary_colors: string;
  logo_url: string;
  visual_style: string;
  brand_story: string;
  brand_values: string;
  is_active: boolean;
  sku_count?: number;
  created_by?: number;
  created_by_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BrandListItem {
  id: number;
  name: string;
  archetype: string;
  tone: string;
  is_active: boolean;
  sku_count: number;
  created_at: string;
  updated_at: string;
}

export interface BrandSimple {
  id: number;
  name: string;
  archetype: string;
  is_active: boolean;
}

/**
 * 获取所有品牌（完整列表）
 */
export const getAllBrands = async (params?: {
  is_active?: boolean;
  search?: string;
}): Promise<BrandListItem[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/brands/`, { params });
    // 使用统一工具函数提取数据
    return extractResultsFromResponse<BrandListItem>(response.data);
  } catch (error: any) {
    console.error('获取品牌列表失败:', error);
    throw error;
  }
};

/**
 * 获取简化的品牌列表（用于下拉选择）
 */
export const getSimpleBrands = async (isActive: boolean = true): Promise<BrandSimple[]> => {
  const response = await axios.get(`${API_BASE_URL}/brands/simple_list/`, {
    params: { is_active: isActive }
  });
  return response.data;
};

/**
 * 获取单个品牌详情
 */
export const getBrandDetail = async (id: number): Promise<Brand> => {
  const response = await axios.get(`${API_BASE_URL}/brands/${id}/`);
  return response.data;
};

/**
 * 创建品牌
 */
export const createBrand = async (brand: Partial<Brand>): Promise<Brand> => {
  const response = await axios.post(`${API_BASE_URL}/brands/`, brand);
  return response.data;
};

/**
 * 更新品牌
 */
export const updateBrand = async (id: number, brand: Partial<Brand>): Promise<Brand> => {
  const response = await axios.put(`${API_BASE_URL}/brands/${id}/`, brand);
  return response.data;
};

/**
 * 删除品牌
 */
export const deleteBrand = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE_URL}/brands/${id}/`);
};

/**
 * 切换品牌激活状态
 */
export const toggleBrandActive = async (id: number): Promise<Brand> => {
  const response = await axios.post(`${API_BASE_URL}/brands/${id}/toggle_active/`);
  return response.data;
};

/**
 * 获取品牌关联的SKU列表
 */
export const getBrandSKUs = async (id: number): Promise<{
  brand: string;
  sku_count: number;
  skus: Array<{
    id: number;
    name: string;
    asin: string;
    category: string;
  }>;
}> => {
  const response = await axios.get(`${API_BASE_URL}/brands/${id}/skus/`);
  return response.data;
};

